// Wire types — snake_case, mirroring the .NET DTOs exactly. Do not camelCase these;
// the backend serialises with [JsonPropertyName] and any drift binds silently to null.

export interface AuthUser {
  id: string;
  email: string;
  display_name: string;
  role: string;
  must_change_password: boolean;
  permissions: string[];
}

export interface AuthResponse {
  access_token: string;
  access_token_expires_at: string;
  refresh_token: string;
  refresh_token_expires_at: string;
  user: AuthUser;
}

export interface TenantSearchHit {
  tenant_id: string;
  name: string | null;
  environment: string | null;
  owner_email: string | null;
  plan_code: string | null;
  is_internal: boolean;
  synced_at: string;
}

export interface TenantSearchResponse {
  tenants: TenantSearchHit[];
}

export interface SourceResponse {
  id: string;
  provider: string;
  site_key: string;
  display_name: string;
  reporting_time_zone: string;
  enabled: boolean;
  last_ingested_date: string | null;
  /** Clarity project id — PUBLIC (it ships in the page snippet). Used to deep-link recordings. */
  project_id?: string | null;
}

export interface DailyPoint {
  date: string;
  metrics: Record<string, number>;
}

export interface BreakdownRow {
  value: string;
  metrics: Record<string, number>;
}

export interface OverviewResponse {
  site_key: string;
  from: string;
  to: string;
  totals: Record<string, number>;
  series: DailyPoint[];
  /** Keyed by Dimensions.* — every breakdown present in the range. Feeds all four tabs. */
  breakdowns: Record<string, BreakdownRow[]>;
  providers_included: string[];
  /** Days with no ingested rows. Must be surfaced — a gap must never render as a dip. */
  missing_dates: string[];
  /** Days still in progress (today). Marked so a partial day is not read as a decline. */
  provisional_dates: string[];
}

export interface Account {
  tenant_id: string;
  name: string | null;
  use_type: string;
  owner_email: string | null;
  owner_name: string | null;
  owner_user_id: string | null;
  /** Measured storage. null = not collected; 0 = genuinely nothing stored. */
  storage_used_bytes: number | null;
  storage_file_count: number | null;
  ai_balance_credits: number | null;
  ai_held_credits: number | null;
  ai_lifetime_debited: number | null;
  created_at: string;
  /** null = could not read that tenant's database. NOT zero. */
  user_count: number | null;
  active_user_count: number | null;
  pending_invitation_count: number | null;
  count_error: string | null;
  /** From the tenant's own audit log — the platform's feature-usage record. */
  event_count: number;
  event_count_30d: number;
  last_activity_at: string | null;
  login_count: number;
  last_login_at: string | null;
  apps_enabled: string[];
  /** Apps actually opened. The gap vs apps_enabled is a failed rollout. */
  apps_used: string[];
  /** Internal environment rather than a customer. */
  is_internal: boolean;
  synced_at: string;
  /** The environment the replica places this tenant on; null for a row collected before environments existed. */
  environment?: string | null;

  /** Replicated subscription. Always present — check `is_replicated` inside it. */
  subscription: AccountSubscription;

  /** Live users + pending invitations. Null when either count is unknown — never a partial sum. */
  seats_used: number | null;
  /** Null when the limit is unenforced OR usage is unknown. 0 would read as "unused". */
  seat_utilisation: number | null;
  is_at_seat_limit: boolean;
  /** Entitled but never opened — a failed rollout, or a downgrade waiting to happen. */
  entitled_unused_apps: string[];
  /** Enabled in tenant_apps but not entitled — the projection has drifted. */
  drifted_apps: string[];
  /** Minor units. Null = not replicated; 0 = non-billable plan. */
  mrr_minor: number | null;
  /** Negative once the trial has lapsed — kept, because that is what drives the follow-up. */
  trial_days_remaining: number | null;
  needs_attention: boolean;
}

export interface AccountOverride {
  app_key: string;
  /** grant | deny — deny wins over both the plan and a grant. */
  effect: string;
}

export interface AccountSubscription {
  /**
   * False when no subscription has been replicated for this tenant.
   *
   * NOT the same as "free": anyware.iam's feature gate fails OPEN for a tenant with no upstream
   * row, so this flags a tenant whose entitlements are currently unenforced.
   */
  is_replicated: boolean;
  plan_code: string | null;
  /** trialing | active | past_due | suspended | cancelled | churned */
  status: string | null;
  billing_cycle: string | null;
  /** Integer minor units. Format at the edge; never carry money as a float. */
  price_minor: number | null;
  currency: string | null;
  /** Null = unenforced upstream, NOT zero. */
  seat_limit: number | null;
  trial_ends_at: string | null;
  current_period_end: string | null;
  grace_period_ends_at: string | null;
  cancel_at_period_end: boolean | null;
  retention_until: string | null;
  apps_entitled: string[];
  overrides: AccountOverride[];
  is_billable: boolean | null;
}

/** Revenue and chase-list figures over the accounts in view. */
export interface SubscriptionRollup {
  /** Minor units, internal/non-billable plans excluded. */
  mrr_minor: number;
  mrr_currency: string | null;
  /**
   * True when more than one currency is present, in which case `mrr_minor` covers only
   * `mrr_currency` and is INCOMPLETE.
   */
  mrr_is_mixed_currency: boolean;
  trials_ending_within_7_days: number;
  /** Still trialing with the end date behind us — IAM's sweep is behind, or not running. */
  trials_lapsed: number;
  past_due: number;
  suspended: number;
  at_seat_limit: number;
  /** Accounts with no replicated subscription — the size of IAM's fail-open hole. */
  missing_subscriptions: number;
  paying_for_unused_apps: number;
  purge_eligible: number;
}

export interface SubscriptionOverrideDetail {
  app_key: string;
  effect: string;
  reason: string | null;
  expires_at: string | null;
  created_by: string | null;
  created_at: string;
}

export interface SubscriptionEventDetail {
  kind: string;
  summary: string;
  from_value: string | null;
  to_value: string | null;
  actor_label: string | null;
  occurred_at: string;
}

export interface PlanOption {
  plan_code: string;
  display_name: string;
  apps: string[];
  /** Null = unlimited, never zero. */
  seat_limit: number | null;
  ai_credit_grant: number | null;
  storage_limit_mb: number | null;
  list_price_minor: number;
  is_billable: boolean;
}

/**
 * LIVE subscription detail from anyware.iam — the one read on the account page that is not served
 * from the replica. Carries what a replica cannot: today's seat count, override reasons, the
 * authoritative event trail, and whether the plan is still in IAM's catalog.
 */
export interface SubscriptionDetail {
  tenant_id: string;
  plan_code: string;
  plan_display_name: string;
  /** False when the plan has been retired upstream — ops needs to see that. */
  plan_is_known: boolean;
  is_billable: boolean;
  status: string;
  billing_cycle: string;
  price_minor: number;
  currency: string;
  seat_limit: number | null;
  /** Null means UNKNOWN (unreadable tenant DB), never zero. */
  seats_used: number | null;
  ai_credit_grant: number | null;
  storage_limit_mb: number | null;
  api_rate_tier: string | null;
  trial_start_at: string | null;
  trial_ends_at: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  grace_period_ends_at: string | null;
  cancel_at_period_end: boolean;
  cancelled_at: string | null;
  churned_at: string | null;
  retention_until: string | null;
  billing_provider: string | null;
  notes: string | null;
  /** plan ∪ grants − denies, resolved by IAM. */
  resolved_apps: string[];
  catalog_apps: string[];
  overrides: SubscriptionOverrideDetail[];
  events: SubscriptionEventDetail[];
  updated_at: string;
}

export interface AccountsResponse {
  accounts: Account[];
  total_accounts: number;
  total_users: number;
  new_accounts: number;
  has_count_errors: boolean;
  customer_accounts: number;
  internal_accounts: number;
  active_accounts_7d: number;
  subscriptions: SubscriptionRollup;
}

export interface MatrixResponse {
  row_dimension: string;
  col_dimension: string;
  metric: string;
  row_keys: string[];
  col_keys: string[];
  cells: { row: string; col: string; value: number }[];
}

export interface PagedBreakdown {
  dimension: string;
  rows: BreakdownRow[];
  total: number;
  page: number;
  per_page: number;
  /** Filtered rows come from GA4 cross-tabs only — Clarity metrics will be absent. */
  is_filtered: boolean;
}

export interface AvailableFilter {
  dimension: string;
  applies_to: string[];
  values: string[];
}

export interface AccountUser {
  user_id: string;
  display_name: string | null;
  email: string | null;
  status: string | null;
  event_count: number;
  apps_used: number;
  last_active_at: string | null;
  created_at: string | null;
  /** `app:Profile` pairs. The tenant-wide Owner has no app and reads as `-:Owner`. */
  roles: string[];
  is_operator: boolean;
  is_owner: boolean;
  /** null = never signed in, which is NOT the same as inactive. */
  first_login_at: string | null;
  has_never_signed_in: boolean;
  last_device: string | null;
}

/** One day of activity. `date` is a plain day (yyyy-MM-dd) with no timezone. */
export interface AccountDailyActivity {
  date: string;
  events: number;
  active_users: number;
  logins: number;
}

/** Dated feature usage — filtered by range client-side. */
/** One day's count of one (app, action) — no feature: the per-feature ranking is `api.telemetry.tenantFeatures`. */
export interface AccountFeatureUsage {
  date: string;
  app_key: string;
  action: string;
  event_count: number;
}

export interface AccountUsage {
  app_key: string;
  action: string;
  event_count: number;
}

/**
 * One feature's usage — a feature being the `(app_key, target_type)` pair.
 *
 * The action is the verb and the target type is the noun. Ranking verbs alone gives
 * "created, updated, deleted" for every account on the platform, which is true and useless.
 */
export interface FeatureRank {
  app_key: string;
  target_type: string;
  display_name: string;
  /** False when nobody has annotated this feature — shown as a flag, not hidden. */
  is_known: boolean;
  event_count: number;
  /** Distinct people who used it. Absent on the per-agent view, where it is always one. */
  user_count?: number | null;
  /** Days this agent touched it — separates "once, heavily" from "every day". */
  active_days?: number | null;
}

export interface PlatformFeature {
  app_key: string;
  target_type: string;
  display_name: string;
  is_known: boolean;
  event_count: number;
  tenants_using: number;
  /** Null when there is no denominator. NOT 0 — that would claim nobody uses it. */
  tenant_share: number | null;
}

export interface PlatformAdoption {
  features: PlatformFeature[];
  /** The denominator behind every tenant_share. Shown, so the share can be checked. */
  tenants_in_scope: number;
  includes_internal: boolean;
  oldest_sync: string | null;
  newest_sync: string | null;
  from: string;
  to: string;
}

/** A registered feature no tenant has ever used — the dead-code list. */
export interface UnusedFeature {
  app_key: string;
  target_type: string;
  display_name: string;
  is_known: boolean;
  last_seen_at: string | null;
}

/**
 * One tenant's single most-used feature in the window. A tenant with nothing in the window is
 * ABSENT from the list — the scoreboard shape — and renders as an em dash, never a made-up name.
 */
export interface TenantTopFeature {
  tenant_id: string;
  app_key: string;
  target_type: string;
  display_name: string;
  /** False when nobody has annotated this feature — shown as a flag, not hidden. */
  is_known: boolean;
  event_count: number;
}

export interface AccountDetail {
  account: Account;
  /** Null when the role may not see named people (`users_withheld`) — never an empty tenant. */
  users: AccountUser[] | null;
  users_withheld?: boolean;
  usage: AccountUsage[];
  daily_activity: AccountDailyActivity[];
  feature_usage: AccountFeatureUsage[];
}

/**
 * A tenant's presence as of the last collector run.
 *
 * `synced_at` must be rendered wherever these numbers are. They describe a replica refreshed every
 * few hours, so `online_now` means "was live as of that timestamp" — shown bare, a three-hour-old
 * count reads as a live one.
 */
export interface Presence {
  online_now: number;
  /** Signed in since midnight UTC. The timezone is stated on screen, never implied. */
  active_today: number;
  total_seats: number;
  active_seats: number;
  /** No login in 30 days, or none ever. */
  dead_seats: number;
  never_signed_in: number;
  /** Null = unenforced upstream, which is not zero. */
  seat_limit: number | null;
  /** Null when there is no limit to divide by. Never render as 0%. */
  seat_utilization: number | null;
  synced_at: string | null;
  /** False = every count above is UNKNOWN, not zero. The UI must say so. */
  sessions_collected: boolean;
}

export interface Seat {
  user_id: string;
  display_name: string | null;
  email: string | null;
  /** active | dormant | never_signed_in */
  state: string;
  last_login_at: string | null;
  /** Null for a seat never used — "never" is not a long duration. */
  days_since_login: number | null;
  last_device: string | null;
  /**
   * No `last_country` here, deliberately — the seats route is gated on plain `telemetry:read` and
   * per-person location belongs behind `agent_detail`. It is on `UserSession` instead.
   */
  is_live: boolean;
}

export interface UserSession {
  login_session_id: string;
  login_at: string;
  logout_at: string | null;
  expires_at: string;
  status: string;
  access_type: string;
  os: string | null;
  browser: string | null;
  device_type: string | null;
  country: string | null;
  is_live: boolean;
  /** Null while the session is still open — a running session has no length yet. */
  duration_minutes: number | null;
  ended_by_admin: boolean;
  terminated_reason: string | null;
}

export interface UserSessions {
  sessions: UserSession[];
  /** Every session ever — NOT sessions.length, which is capped at the request limit. */
  session_count: number;
  /** The denominator behind avg_session_minutes. Shown with it, always. */
  closed_session_count: number;
  avg_session_minutes: number | null;
  first_login_at: string | null;
  last_login_at: string | null;
  admin_terminated_count: number;
  distinct_device_count: number;
  live_session_count: number;
  /** False = the totals are a floor from the stored rows, not a count. */
  stats_collected: boolean;
  /** True when history exists beyond the returned rows. */
  truncated: boolean;
  synced_at: string | null;
}

export interface DeviceSlice {
  /** device_type | browser | os | access_type */
  kind: string;
  value: string;
  sessions: number;
  /** Distinct tenants contributing. Null on a single-tenant read. */
  tenants: number | null;
}

/**
 * One move between two FEATURES — an `(app_key, target_type)` pair, not a raw action.
 *
 * This is the path of CHANGES. `audit_events` records write actions, so reading a record leaves no
 * row: an agent who spent an hour reading and edited one field is a single step here. Every surface
 * rendering this must carry the qualifier.
 */
export interface FeatureTransition {
  from_feature: string;
  to_feature: string;
  from_display_name: string;
  to_display_name: string;
  count: number;
  /** Distinct agents who made this move. Absent on a per-agent read, where it is always one. */
  user_count?: number | null;
}

/** An ordered opening prefix of a working session — what the Sunburst draws as rings. */
export interface EntryPath {
  steps: string[];
  display_names: string[];
  sessions: number;
}

export interface Flows {
  transitions: FeatureTransition[];
  /** A→A moves. Excluded from `transitions`, counted here — they are work, but not a path. */
  self_transitions: number;
  /** Every counted move, including self-transitions and the folded tail. */
  total_transitions: number;
  /** Moves folded into `(other)` at collection time. Bounded coverage, announced. */
  folded_transitions: number;
  entry_paths: EntryPath[];
  synced_at: string | null;
  /** The rule that defined a working session. Rendered from here, never hardcoded in a caption. */
  working_session_gap_minutes: number;
}

export interface AgentFlows {
  flows: Flows;
  sessions: number;
  steps: number;
  /** Mean steps per working session. Null = nothing observed, which is not zero steps. */
  avg_steps: number | null;
  entry_feature: string | null;
  exit_feature: string | null;
  entry_display_name: string | null;
  exit_display_name: string | null;
  /** Operations spanning more than one app, matched by correlation id. */
  cross_service_operations: number;
  /** False = the numbers above are absences standing in as zeros. */
  shape_collected: boolean;
}

export interface PlatformTransition {
  from_feature: string;
  to_feature: string;
  from_display_name: string;
  to_display_name: string;
  count: number;
  tenants_using: number;
  /** Null when there is no denominator. Never 0. */
  tenant_share: number | null;
}

export interface PlatformFlows {
  transitions: PlatformTransition[];
  tenants_in_scope: number;
  includes_internal: boolean;
  working_session_gap_minutes: number;
}

/**
 * One agent's sharing.
 *
 * `is_isolated` is null when the share tables were not read — NOT false. Naming somebody as working
 * alone on the strength of a query that never ran is the kind of wrong that gets acted on.
 */
export interface AgentCollaboration {
  user_id: string;
  display_name: string | null;
  email: string | null;
  shared_out: number | null;
  shared_with: number | null;
  /** Grants by target KIND — type keys only, never object titles. */
  target_kinds: Record<string, number>;
  is_isolated: boolean | null;
}

export interface Collaboration {
  active_shares: number | null;
  shared_objects: number | null;
  total_objects: number | null;
  /** Shared over total. Null when there is nothing to divide — never render as 0%. */
  collaboration_ratio: number | null;
  orphaned_shares: number | null;
  /** Agents at neither end of any grant, by name — the list somebody acts on. */
  isolated: AgentCollaboration[];
  total_agents: number;
  synced_at: string | null;
  /** False = every number above is UNKNOWN, not zero. */
  collected: boolean;
}

export interface ModuleVolumePoint {
  collected_at: string;
  record_count: number | null;
}

export interface ModuleVolume {
  /** The stable module id. Never a display name — that is customer-authored text. */
  module_id: string;
  latest: number | null;
  /** Latest minus earliest. Null on a single point — one observation is not a trend. */
  growth: number | null;
  points: ModuleVolumePoint[];
}

export interface AutomationDay {
  date: string;
  runs: number | null;
  failures: number | null;
  /** Null on a day nothing ran — 0% would read as a day that went well. */
  failure_rate: number | null;
}

export interface Configuration {
  custom_modules: number | null;
  custom_fields: number | null;
  layouts: number | null;
  workflows: number | null;
  pipelines: number | null;
  blueprints: number | null;
  /** Sum of what was collected. Null when nothing was. */
  complexity_total: number | null;
  /** Two flags, because they are two databases and either can be absent alone. */
  metadata_collected: boolean;
  workflow_collected: boolean;
  modules: ModuleVolume[];
  automation: AutomationDay[];
  synced_at: string | null;
}

export interface CollaborationDistributionRow {
  tenant_id: string;
  name: string | null;
  collaboration_ratio: number | null;
  active_shares: number | null;
  isolated_agents: number | null;
}

export interface CollaborationDistribution {
  tenants: CollaborationDistributionRow[];
  /** Withheld below the minimum sample — a median over three accounts moves with the fourth. */
  median: number | null;
  tenants_in_scope: number;
  /** The denominator behind the median: how many were actually measurable. */
  tenants_with_ratio: number;
  includes_internal: boolean;
}

export interface DeviceMix {
  slices: DeviceSlice[];
  /**
   * Every session in scope, including those whose device fields were not collected — so this is
   * deliberately >= the sum of the slices. That gap is the uncollected share and stays visible.
   */
  total_sessions: number;
  tenants_in_scope: number | null;
  includes_internal: boolean;
}

export interface AccountNotification {
  id: string;
  tenant_id: string;
  tenant_name: string | null;
  /** user_added | user_removed | tenant_added | app_enabled */
  kind: string;
  summary: string;
  subject_id: string | null;
  detected_at: string;
  /** Null = unread. Read state is shared by every admin. */
  acknowledged_at?: string | null;
}

export interface PlatformService {
  container_name: string;
  stack: string;
  service_name: string;
  image: string | null;
  state: string;
  status_text: string | null;
  /** Climbing count = crash loop, even while state reads "running". */
  restart_count: number;
  health: string | null;
  started_at: string | null;
  observed_at: string;
}

export interface PlatformStack {
  stack: string;
  total: number;
  running: number;
  unhealthy: number;
  down: number;
}

export interface ServicesResponse {
  services: PlatformService[];
  stacks: PlatformStack[];
  total_services: number;
  running_services: number;
  problem_services: number;
  observed_at: string | null;
  /** Last observation is old enough that the picture may be wrong. */
  is_stale: boolean;
}

export interface Alert {
  id: string;
  /** critical | warning | info */
  severity: string;
  title: string;
  detail: string;
  /** What to do about it. */
  action: string;
}

export interface SourceHealth {
  source_id: string;
  provider: string;
  enabled: boolean;
  last_ingested_date: string | null;
  fetches_used_today: number;
  daily_fetch_budget: number;
  requests_per_fetch: number;
  supports_backfill: boolean;
  missing_dates: string[];
}

/** Dimension keys, mirroring Application/Analytics/ProviderFact.cs DimensionNames. */
export const Dimensions = {
  country: 'country',
  source: 'source',
  medium: 'medium',
  campaign: 'campaign',
  referrer: 'referrer',
  pagePath: 'page_path',
  pageTitle: 'page_title',
  device: 'device',
  browser: 'browser',
  operatingSystem: 'operating_system',
  language: 'language',
  landingPage: 'landing_page',
  channelGroup: 'channel_group',
  city: 'city',
  newVsReturning: 'new_vs_returning',
  hourOfDay: 'hour_of_day',
  dayOfWeek: 'day_of_week',
  hostname: 'hostname',
} as const;

export interface RealtimeMinute {
  minutes_ago: number;
  active_users: number;
}

export interface RealtimeBreakdown {
  value: string;
  active_users: number;
}

/**
 * A sliding ~30-minute window. GA4 only — Clarity has no realtime API.
 * Never stored: it is a snapshot, not a fact, and cannot be re-requested for a past moment.
 */
export interface RealtimeResponse {
  active_users: number;
  page_views: number;
  by_minute: RealtimeMinute[];
  top_pages: RealtimeBreakdown[];
  captured_at: string;
}

/** Metric keys, mirroring Application/Analytics/ProviderFact.cs MetricNames. */
export const Metrics = {
  // GA4
  sessions: 'sessions',
  activeUsers: 'active_users',
  newUsers: 'new_users',
  engagedSessions: 'engaged_sessions',
  averageSessionDuration: 'average_session_duration',
  conversions: 'conversions',
  screenPageViews: 'screen_page_views',
  // Clarity
  totalSessionCount: 'total_session_count',
  /** Clarity's page views — a separate key from GA4's, so the two are never summed. */
  clarityPageViews: 'clarity_page_views',
  totalBotSessionCount: 'total_bot_session_count',
  distinctUserCount: 'distinct_user_count',
  pagesPerSession: 'pages_per_session',
  activeEngagementTime: 'active_engagement_time',
  rageClickCount: 'rage_click_count',
  deadClickCount: 'dead_click_count',
  excessiveScrollCount: 'excessive_scroll_count',
  quickbackClickCount: 'quickback_click_count',
  scriptErrorCount: 'script_error_count',
  errorClickCount: 'error_click_count',
  sessionsWithMetricPct: 'sessions_with_metric_pct',
  bounceRate: 'bounce_rate',
  engagementRate: 'engagement_rate',
  userEngagementDuration: 'user_engagement_duration',
  // First-party (tavi_leads) — the only source that knows about actual conversions.
  leads: 'leads',
  uniqueVisitors: 'unique_visitors',
  averageScrollDepth: 'average_scroll_depth',
  averageEngagementTime: 'average_engagement_time',
} as const;

export const PERMISSIONS = {
  analyticsRead: 'controlplane:analytics:read',
  analyticsExport: 'controlplane:analytics:export',
  sourceManage: 'controlplane:source:manage',
  ingestionRun: 'controlplane:ingestion:run',
  /** See commercial terms — agreed price, discounts, contract notes. Owner + Analyst. */
  subscriptionRead: 'controlplane:subscription:read',
  /** Change plan / seats / trial / overrides / status. Owner only — suspend locks a tenant out. */
  subscriptionManage: 'controlplane:subscription:manage',
  /** Work the pipeline: move a lead's stage, set its value, assign an owner. Owner + Analyst. */
  leadsManage: 'controlplane:leads:manage',
  /** Take telemetry out of the system as a file. Owner + Analyst. */
  telemetryExport: 'controlplane:telemetry:export',
  /** Tenant health scores and the at-risk queue. Owner + Analyst + Viewer. */
  telemetryRead: 'controlplane:telemetry:read',
  /** Named people — each user's e-mail, roles and device, a tenant's owner contact. Owner + Analyst. */
  telemetryAgentDetail: 'controlplane:telemetry:agent_detail',
  /** See the console's admins and their change log. Owner only. */
  adminsRead: 'controlplane:admins:read',
  /** Add admins, change roles, disable, reset passwords, unlock. Owner only. */
  adminsManage: 'controlplane:admins:manage',
  /** Use the explore surface — compose dimension × measure questions. Owner + Analyst. */
  exploreRead: 'controlplane:explore:read',
  /** Save and delete SHARED explore views. Owner only — a saved view shapes what the team sees. */
  exploreManage: 'controlplane:explore:manage',

  // ─── Tenant ops (control plane v1 — docs/api-tenant-ops.md §1) ───
  /** Provisioning jobs, reveal-password, read the step catalog + industry packs. Owner only. */
  tenantProvision: 'controlplane:tenant:provision',
  /** Environments CRUD + internal key, PUT step catalog, import keys. Owner only (CP4). */
  environmentsManage: 'controlplane:environments:manage',
  /** Environments (read), host overview/series, environment tenants, footprint. All roles. */
  hostsRead: 'controlplane:hosts:read',
  /** Products panel, rating/*, costs/summary, ledger, the calculator. Owner + analyst. */
  costsRead: 'controlplane:costs:read',
  billingRead: 'controlplane:billing:read',
  /** Billing admin writes — money writes carry a reason (CP5). Owner only. */
  billingManage: 'controlplane:billing:manage',
  messagingRead: 'controlplane:messaging:read',
  messagingManage: 'controlplane:messaging:manage',
} as const;

// ─── First-party RUM ────────────────────────────────────────────────────────
// The realtime half of the analytics story. Clarity has no realtime API and no backfill, so these
// come from the control plane's own tracker (served at /rum.js) rather than from a provider.

export interface RumCount {
  value: string;
  count: number;
}

export interface RumMinute {
  minutes_ago: number;
  sessions: number;
  events: number;
}

/** One heatmap cell in normalised viewport space — see `heat_grid` for the resolution. */
export interface RumHeatCell {
  col: number;
  row: number;
  count: number;
  /** Rage clicks in this cell. Overlaid, not summed into `count`. */
  rage_count: number;
}

export interface RumLive {
  site_key: string;
  as_of: string;
  window_minutes: number;
  active_sessions: number;
  events: number;
  page_views: number;
  clicks: number;
  frustrations: number;
  /** PER SESSION, not a percentage — can exceed 1. Null when nothing is active. */
  frustrations_per_session: number | null;
  top_pages: RumCount[];
  top_targets: RumCount[];
  by_country: RumCount[];
  by_device: RumCount[];
  by_language: RumCount[];
  by_kind: RumCount[];
  per_minute: RumMinute[];
  heatmap: RumHeatCell[];
  /** Grid resolution, e.g. 40 ⇒ a 40×40 grid. Read from the payload, never hardcoded. */
  heat_grid: number;
}

export interface RumRollup {
  period_start: string;
  grain: 'day' | 'month';
  sessions: number;
  pages: number;
  page_views: number;
  clicks: number;
  rage_clicks: number;
  dead_clicks: number;
  excessive_scrolls: number;
  quickbacks: number;
  script_errors: number;
  form_starts: number;
  form_submits: number;
  form_abandons: number;
  cta_clicks: number;
  outbound_clicks: number;
  text_copies: number;
  language_switches: number;
  /** Null when nothing was measured — NOT zero, which would read as "nobody scrolled". */
  average_scroll_depth: number | null;
  average_engagement_ms: number | null;
  frustrations_per_session: number | null;
  form_completion_rate: number | null;
  /** Top-N slices: targets, sections, countries, devices, languages, pages, errors, heat, vitals. */
  breakdown: Record<string, Record<string, number>>;
  first_event_at: string;
  last_event_at: string;
  updated_at: string;
}

// ─── Domains & journeys ─────────────────────────────────────────────────────

export interface PathVariant {
  path: string;
  value: number;
  /** Share of its canonical group, 0–1 — the "/ is 68%, /home is 32%" number. */
  share: number;
}

/** One page after alias folding, carrying the raw paths that were merged into it. */
export interface PathGroup {
  canonical: string;
  total: number;
  variants: PathVariant[];
}

export interface DomainSummary {
  hostname: string;
  metrics: Record<string, number>;
  top_pages: PathGroup[];
  landing_pages: PathGroup[];
  top_countries: BreakdownRow[];
  top_channels: BreakdownRow[];
}

export interface DomainComparison {
  /** False until a GA4 ingestion has run since `hostname` was added — NOT the same as "no traffic". */
  has_data: boolean;
  domains: DomainSummary[];
  path_groups: PathGroup[];
}

export interface Journey {
  /** e.g. "/ > /contact > /aboutus" */
  journey: string;
  steps: string[];
  step_count: number;
  sessions: number;
  /** Share of ALL journeys in scope, so the long tail stays visible. */
  share: number;
}

export interface Transition {
  from: string;
  to: string;
  /** 1 = the first hop of a visit. */
  position: number;
  sessions: number;
}

export interface JourneysResponse {
  journeys: Journey[];
  transitions: Transition[];
  countries: string[];
  hostnames: string[];
  total_sessions: number;
  country: string | null;
  hostname: string | null;
}

// ─── Session behaviour: enter → exit ────────────────────────────────────────

export interface DurationBucket {
  label: string;
  lower_ms: number;
  /** Null on the final, open-ended bucket. */
  upper_ms: number | null;
  sessions: number;
  /** Share of MEASURED sessions — the buckets sum to 1. */
  share: number;
}

export interface EntryExitPair {
  entry: string;
  exit: string;
  sessions: number;
  share: number;
  /** Entry equals exit. Flagged, not hidden — usually the largest class. */
  single_page: boolean;
}

export interface ExitReasonCount {
  /** A tracker exit reason, or "(unknown)" when the exit was never observed. */
  reason: string;
  sessions: number;
  share: number;
}

export interface SessionBehaviour {
  grain: 'day' | 'month';
  from: string;
  to: string;
  country: string | null;
  hostname: string | null;

  sessions: number;
  /** Rows written before the tracker measured anything. The caveat on every percentile below. */
  sessions_without_duration: number;
  bounces: number;
  bounce_rate: number | null;

  /** VISIBLE time. This is the one to label "time on site". */
  mean_engaged_ms: number | null;
  median_engaged_ms: number | null;
  p75_engaged_ms: number | null;
  p90_engaged_ms: number | null;

  /** Wall-clock, hidden tabs included. NEVER present this as time on site. */
  median_span_ms: number | null;

  buckets: DurationBucket[];
  entry_exit: EntryExitPair[];
  exit_reasons: ExitReasonCount[];
  countries: string[];
  hostnames: string[];
}

// ─── Time on page ───────────────────────────────────────────────────────────

export interface PageEngagementRow {
  page: string;
  variants: PathVariant[];
  /** Seconds per page view from GA4. Null = not measured, NOT zero. */
  ga4_avg_seconds: number | null;
  ga4_total_seconds: number | null;
  ga4_views: number | null;
  /** Seconds per page view from the first-party tracker. Null = not measured. */
  rum_avg_seconds: number | null;
  rum_total_seconds: number | null;
  rum_views: number | null;
  /** RUM ÷ GA4. Above 1 estimates engagement GA4 cannot see — a comparison, not a correction. */
  ratio: number | null;
}

export interface PageEngagementResponse {
  has_ga4: boolean;
  /** False until the first-party tracker is live and has collected dwell. */
  has_rum: boolean;
  /** Dwell that overflowed the page cap — surfaced rather than silently dropped. */
  untracked_rum_seconds: number | null;
  rows: PageEngagementRow[];
}

// ─── Leads ──────────────────────────────────────────────────────────────────
// Row-level leads with the sales state the control plane owns. Distinct from the `leads` METRIC
// in daily_metrics: that one is a daily count and the conversion-rate numerator; these are the
// individual people, with a stage, a value, and — for widget chats — how the conversation went.

export type LeadSourceKey = 'form' | 'chat';

/** Pipeline stages, in order. Mirrors LeadStatus on the server. */
export const LEAD_STATUSES = ['new', 'contacted', 'qualified', 'won', 'lost'] as const;
export type LeadStatusKey = (typeof LEAD_STATUSES)[number];

export interface LeadRow {
  id: string;
  /** 0-100 AI intent score. null = not scored. A SUGGESTION, never the sales status. */
  ai_score?: number | null;
  ai_score_reason?: string | null;
  ai_scored_at?: string | null;
  source: LeadSourceKey;
  external_id: string;
  captured_at: string;

  name: string | null;
  email: string | null;
  phone: string | null;
  country: string | null;
  city: string | null;
  language: string | null;
  channel: string | null;
  form_type: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  landing_page: string | null;
  referrer: string | null;

  conversation_started_at: string | null;
  conversation_closed_at: string | null;
  /** Visitor's first message → first human reply. Null on form leads and unanswered chats. */
  first_response_seconds: number | null;
  /** Whole conversation, start to close. Null while it is still open. */
  handling_seconds: number | null;
  queue_seconds: number | null;
  messages_in: number | null;
  messages_out: number | null;
  csat_score: number | null;
  conversation_status: string | null;
  is_missed: boolean;

  status: LeadStatusKey;
  value_amount: number | null;
  currency: string | null;
  owner: string | null;
  notes: string | null;
  status_changed_at: string | null;
}

export interface LeadPageResponse {
  items: LeadRow[];
  total: number;
  page: number;
  per_page: number;
}

export interface LeadBreakdownRow {
  value: string;
  leads: number;
  won: number;
  won_value: number;
  avg_first_response_seconds: number | null;
}

export interface LeadSeriesPoint {
  date: string;
  leads: number;
  won: number;
  won_value: number;
}

export interface LeadsSummaryResponse {
  site_key: string;
  from: string;
  to: string;

  total: number;
  by_status: Record<string, number>;
  by_source: Record<string, number>;

  pipeline_value: number;
  won_value: number;
  won_count: number;
  lost_count: number;
  /** Won ÷ decided. Null until something has been won or lost — never 0% by default. */
  win_rate: number | null;
  average_won_value: number | null;

  avg_first_response_seconds: number | null;
  median_first_response_seconds: number | null;
  avg_handling_seconds: number | null;
  median_handling_seconds: number | null;
  avg_queue_seconds: number | null;
  avg_csat: number | null;
  missed_count: number;

  by_country: LeadBreakdownRow[];
  by_campaign: LeadBreakdownRow[];
  by_language: LeadBreakdownRow[];
  by_channel: LeadBreakdownRow[];
  by_owner: LeadBreakdownRow[];
  series: LeadSeriesPoint[];

  hour_histogram: number[];
  weekday_histogram: number[];

  currency: string | null;
  /** The summary hit its row cap — the figures describe a truncated set and must say so. */
  truncated: boolean;
  countries: string[];
  statuses: LeadStatusKey[];
}

export interface LeadSyncResult {
  source: LeadSourceKey;
  attempted: boolean;
  fetched: number;
  written: number;
  skip_reason: string | null;
  error: string | null;
}

/** The sales columns a human may change. Omitted fields are left untouched by the server. */
export interface LeadPatch {
  status?: LeadStatusKey;
  value_amount?: number;
  currency?: string;
  owner?: string;
  notes?: string;
  clear_value?: boolean;
  clear_owner?: boolean;
}


/** One AI finding, with the evidence that produced it. */
export interface LeadAnalysisFinding {
  title: string;
  detail: string;
  /** Required by the response schema — a recommendation whose numbers cannot be checked is worse than none. */
  evidence: string;
  severity?: string | null;
}

export interface LeadAnalysisContent {
  summary: string;
  findings: LeadAnalysisFinding[];
}

export interface LeadAnalysisItem {
  /** daily_digest | campaigns | service_quality */
  kind: string;
  analysis_date: string;
  generated_at: string;
  model: string | null;
  prompt_version: string | null;
  /** How many leads produced it — four vs four hundred should be read differently. */
  input_lead_count: number;
  content: LeadAnalysisContent | null;
}

export interface LeadInsightsResponse {
  /** False when no API key is set: the panel says "not configured" rather than erroring. */
  is_configured: boolean;
  model: string | null;
  analyses: LeadAnalysisItem[];
}

export interface LeadAnalysisRunResponse {
  analyses_produced: number;
  leads_scored: number;
  note: string | null;
}

// ─── Lead profile ───────────────────────────────────────────────────────────
// One lead in full: the transparent score with its workings, the story in order, how it compares
// with its peers, and who else looks like it. Mirrors LeadProfileResponse on the server.

/** One component of the transparent score. */
export interface LeadScoreFactor {
  key: string;
  label: string;
  points: number;
  /** 0 = the factor does not APPLY to this lead (a form lead has no response time). Not "scored 0". */
  max_points: number;
  detail: string;
}

export type LeadScoreBand = 'hot' | 'warm' | 'cold';

export interface LeadScoreProfile {
  score: number;
  band: LeadScoreBand;
  headline: string;
  earned: number;
  /** Sum of the factors that applied — the denominator behind `score`. */
  applicable_max: number;
  factors: LeadScoreFactor[];
}

export interface LeadTimelineEvent {
  at: string;
  /** captured | chat_started | first_reply | missed | chat_closed | rated | ai_scored | status */
  kind: string;
  title: string;
  detail: string | null;
}

export interface LeadPeerStat {
  key: string;
  label: string;
  value: number | null;
  cohort_median: number | null;
  /** 0–100, already oriented so higher is always better. Null when the cohort is too small. */
  percentile: number | null;
  detail: string | null;
}

export interface RelatedLead {
  id: string;
  label: string;
  source: LeadSourceKey;
  status: LeadStatusKey;
  captured_at: string;
  value_amount: number | null;
  /** Similarity weight — the same function the network graph draws its edges from. */
  weight: number;
  shared: string[];
}

export interface LeadProfileResponse {
  lead: LeadRow;
  score: LeadScoreProfile;
  timeline: LeadTimelineEvent[];
  peers: LeadPeerStat[];
  related: RelatedLead[];
  cohort_size: number;
  cohort_from: string;
  cohort_to: string;
}

// ─── Lead network ───────────────────────────────────────────────────────────
// The similarity graph over the filtered leads. Nodes are leads, edges are shared attributes,
// communities come from label propagation and betweenness from Brandes — see LeadNetworkAnalyzer.

export interface LeadNetworkNode {
  id: string;
  label: string;
  source: LeadSourceKey;
  status: LeadStatusKey;
  country: string | null;
  campaign: string | null;
  value_amount: number | null;
  ai_score: number | null;
  /** -1 when the lead resembles nothing else in the window. */
  community: number;
  degree: number;
  /** 0–1 Brandes betweenness — how much of the graph's shortest-path traffic runs through it. */
  betweenness: number;
  is_bridge: boolean;
}

export interface LeadNetworkEdge {
  source: string;
  target: string;
  weight: number;
  shared: string[];
}

export interface CommunityTrait {
  dimension: string;
  value: string;
  /** 0–1 share of the community carrying this value. */
  share: number;
}

export interface LeadCommunity {
  id: number;
  size: number;
  traits: CommunityTrait[];
  won: number;
  lost: number;
  won_value: number;
  win_rate: number | null;
  avg_first_response_seconds: number | null;
  missed_count: number;
  /** Realised ÷ possible internal edges. Low means a chain, not a cohort. */
  cohesion: number;
  members: string[];
}

export interface LeadNetworkResponse {
  nodes: LeadNetworkNode[];
  edges: LeadNetworkEdge[];
  communities: LeadCommunity[];
  leads_considered: number;
  leads_matched: number;
  truncated: boolean;
  /** Edges ÷ possible edges. Near 1 means the threshold is too loose to separate anything. */
  density: number;
  isolated_count: number;
  edges_omitted: number;
  currency: string | null;
}

// ─── Explore (phase 6) ──────────────────────────────────────────────────────
// Dimension × measure × range × breakdown over the cross-tenant rollups. The dropdowns are built
// from the catalog the SERVER sends — the UI hardcodes no identifier, so the catalog can grow
// without a frontend change. For joins, pivots and scheduled reports, use the BI tool.

export interface ExploreCatalogDimension {
  key: string;
  label: string;
  /** True for the day dimension — it draws a trend and sorts chronologically. */
  is_date: boolean;
}

export interface ExploreCatalogResponse {
  dimensions: ExploreCatalogDimension[];
  measures: { key: string; label: string }[];
  /** Sent so the UI never hardcodes the bounds it displays. */
  row_cap: number;
  max_range_days: number;
}

export interface ExploreFilterSpec {
  dimension: string;
  values: string[];
}

/** The wire-form spec — also exactly what a saved view stores. */
export interface ExploreSpec {
  dimension: string;
  measure: string;
  from: string;
  to: string;
  breakdown?: string | null;
  filters?: ExploreFilterSpec[];
  include_internal?: boolean;
}

export interface ExploreRow {
  /** Stable group key — a `yyyy-MM-dd` day, a tenant id, a vocabulary string. */
  key: string;
  /** What a human reads. Differs from `key` for tenants (the name). */
  label: string;
  break_key: string | null;
  break_label: string | null;
  value: number;
}

export interface ExploreQueryResponse {
  /** Chosen from the result's shape, server-side. The UI draws it; it never picks it. */
  chart: 'trend' | 'bar' | 'heatmap';
  dimension: string;
  measure: string;
  breakdown: string | null;
  from: string;
  to: string;
  include_internal: boolean;
  rows: ExploreRow[];
  /** Always present with `truncated` — a silent cap reads as "that is all there is". */
  row_cap: number;
  truncated: boolean;
}

export interface ExploreSavedView {
  id: string;
  name: string;
  /** The spec as saved — load it straight back into the controls. */
  spec: ExploreSpec;
  created_by: string;
  created_at: string;
}

// --- Phase 5 · scores, risk and benchmarks -------------------------------------------------------

/** One weighted component of a health score, and the words behind it. */
export interface ScoreComponent {
  name: string;
  /** What the policy intended this to be worth, before any rescale. */
  weight: number;
  /** What it actually counted for once absent components were excluded. */
  effective_weight: number;
  /** 0–1, the measured input. */
  value: number;
  points: number;
  /** The component in plain words — what an account manager reads when they ask "why?". */
  explanation: string;
}

/**
 * A component the policy wanted and could not have.
 *
 * Excluded is NOT zero. A missing input drops its component and the rest rescale to carry the whole
 * score, so an account with an unread reporting database is judged on what was readable rather than
 * punished for what was not.
 */
export interface ExcludedComponent {
  name: string;
  weight: number;
  /** Why it is absent — actionable, because it usually points at the collector, not the customer. */
  reason: string;
}

export interface ScorePoint {
  computed_at: string;
  total: number;
  coverage: number;
  score_version: string;
}

export interface Health {
  total: number;
  /** Share of the policy's weight that was measurable. A 90 at 40% is a different claim from a 90. */
  coverage: number;
  /** The policy version that produced this. Stored, so a re-weighting cannot rewrite history. */
  score_version: string;
  components: ScoreComponent[];
  excluded: ExcludedComponent[];
  /** True when NOTHING was measurable — not a score of zero. Render the difference. */
  unscored: boolean;
  inputs_synced_at: string | null;
  computed_at: string;
  /** Oldest first. The trend is worth more than the value. */
  history: ScorePoint[];
}

export type RiskSeverity = 'critical' | 'warning' | 'info';

export interface RiskFlag {
  id: string;
  severity: RiskSeverity;
  title: string;
  /** The measured number in words. What makes the list evidence rather than a verdict. */
  evidence: string;
  action: string;
  value: number | null;
}

export interface Risk {
  flags: RiskFlag[];
  /** The COUNT of raised flags, never a probability — there is no calibration for a percentage. */
  risk_level: number;
}

export interface Milestone {
  milestone: string;
  ordinal: number;
  reached: boolean;
  /** Null while unreached — and also when reached with no recoverable date. */
  reached_at: string | null;
}

export interface Onboarding {
  steps: Milestone[];
  /** Signup to first shared object. Null when either end is unknown. */
  time_to_value_days: number | null;
  synced_at: string | null;
}

export interface AtRiskAccount {
  tenant_id: string;
  name: string | null;
  /** Minor units. What orders the queue — null sorts last, not as zero. */
  mrr_minor: number | null;
  currency: string | null;
  subscription_status: string | null;
  health_score: number | null;
  risk_level: number;
  flags: RiskFlag[];
}

export interface Benchmark {
  metric: string;
  p25: number | null;
  median: number | null;
  p75: number | null;
  p90: number | null;
  /** The denominator. Ships with every figure, always. */
  sample_size: number;
  /** False below `minimum_sample` — the percentiles are then all null and must stay hidden. */
  published: boolean;
  minimum_sample: number;
  computed_at: string;
}

export interface Cohort {
  cohort_month: string;
  tenants: number;
  still_active: number;
  /** Null on an empty cohort — there is nothing to retain. */
  retention_rate: number | null;
}

/**
 * One row of the accounts-list scoreboard.
 *
 * A tenant that has never been scored is ABSENT from the array, not present with a zero — so a
 * client joining on `tenant_id` renders "not scored" rather than "failing".
 */
export interface ScoreboardRow {
  tenant_id: string;
  total: number;
  coverage: number;
  score_version: string;
  /** True when nothing was measurable. Never render this as a low score. */
  unscored: boolean;
  risk_level: number;
  computed_at: string;
}

// ─── Console admins (2026-09-30) ───────────────────────────────────────────

export type AdminRoleName = 'owner' | 'analyst' | 'viewer';

export interface AdminUserView {
  id: string;
  email: string;
  display_name: string;
  role: AdminRoleName | string;
  enabled: boolean;
  must_change_password: boolean;
  /** Set only while the admin is locked out. */
  locked_until: string | null;
  last_login_at: string | null;
  created_at: string;
  is_you: boolean;
}

/** A created or reset admin — the temporary password is in this answer only. */
export interface AdminWithTemporaryPassword {
  admin: AdminUserView;
  temporary_password: string;
}

export interface AdminAuditView {
  id: number;
  at: string;
  actor_id: string;
  actor_name: string;
  action: string;
  target_id: string | null;
  target_name: string | null;
  detail: string | null;
}
